import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { FinnishDateFormat, FinnishShortTimeFormat } from '../../utils/dateHelpers';
import { DELIVERY_TYPES } from '../constants';
import { OrderStatus } from './orderStatus';

/**
 * An order plus the status derived from its timestamps, so both the DataGrid columns and the phone
 * rows can read `orderStatusString` off a row.
 */
export type OrderRow = OrderV2 & { orderStatusString: OrderStatus };

// These keys already exist in both locale files. Keyed on DELIVERY_TYPES so the labels and the
// order-type filter can never disagree about which number means what.
const DELIVERY_TYPE_KEYS: Record<number, string> = {
  [DELIVERY_TYPES.pickup]: 'singleRestaurant.deliveryType.pickup',
  [DELIVERY_TYPES.delivery]: 'singleRestaurant.deliveryType.delivery',
  [DELIVERY_TYPES.eatin]: 'singleRestaurant.deliveryType.eatin',
  [DELIVERY_TYPES.hotStop]: 'singleRestaurant.deliveryType.hotStop',
};

/**
 * "ma 24.8.2026 klo 14.05" — a moment in an order's life, as the detail view shows it. Without
 * seconds on purpose: the timeline card is where the precise timestamps live.
 */
export const formatOrderMoment = (value: Date | string, t: TFunction): string =>
  `${dayjs(value).format('ddd')} ${dayjs(value).format(FinnishDateFormat)} ${t('at')} ${dayjs(value).format(
    FinnishShortTimeFormat
  )}`;

export const getOrderProductCount = (order: OrderV2): number =>
  order.shoppingCartPrice?.orderItemsV2?.reduce((acc, item) => acc + (item.quantity ?? 0), 0) ?? 0;

export const getCustomerName = (order: OrderV2): string =>
  [order.deliveryContact?.firstName, order.deliveryContact?.lastName].filter(Boolean).join(' ');

export const getDeliveryTypeLabel = (order: OrderV2, t: TFunction): string => {
  const key = order.deliveryType !== undefined ? DELIVERY_TYPE_KEYS[order.deliveryType] : undefined;

  return key ? t(key) : '';
};

type OrderTimeLabel = { text: string; isPreorder: boolean };

/**
 * Which timestamp a row's time reading comes from. This is the one piece the two labels below truly
 * shared: a preorder shows the slot it was booked for rather than the moment it was placed, except in
 * the customer-orders view, which lists a customer's history and so always means "when did they
 * order". Returns null when there is no timestamp to show at all.
 */
const selectOrderTime = (
  order: OrderV2,
  customerOrdersView?: boolean
): { value: Date | string; isPreorder: boolean } | null => {
  if (!order.orderPlaced) {
    return null;
  }

  const preOrderTime = customerOrdersView ? undefined : order.preOrderTime;

  return preOrderTime ? { value: preOrderTime, isPreorder: true } : { value: order.orderPlaced, isPreorder: false };
};

/**
 * The "order placed" text for the desktop grid cell, wording included — the column has a header to
 * sit under but no room for a second line, so the preorder marker rides along in the text.
 * `isPreorder` drives the yellow highlight around it.
 */
export const getOrderPlacedLabel = (order: OrderV2, t: TFunction, customerOrdersView?: boolean): OrderTimeLabel => {
  const selected = selectOrderTime(order, customerOrdersView);

  if (!selected) {
    return { text: '-', isPreorder: false };
  }

  const time = `${t('at')} ${dayjs(selected.value).format(FinnishShortTimeFormat)}`;

  if (customerOrdersView) {
    return { text: `${dayjs(selected.value).format(FinnishDateFormat)} - ${time}`, isPreorder: false };
  }

  return {
    text: selected.isPreorder ? `${time} (${t('orderList.Preorder')})` : time,
    isPreorder: selected.isPreorder,
  };
};

/** The same reading in pieces, for the phone row. `date` and `isPreorder` are both optional readings. */
type OrderTimeParts = { time: string; date?: string; isPreorder: boolean };

/**
 * The same reading stripped to the bare clock, for the phone row's left column: it sits above the
 * price in a column of its own, where a "klo" prefix and a parenthesised marker would only add noise.
 * The row composes the preorder marker itself from `isPreorder`.
 *
 * The date comes back as a piece of its own rather than joined to the time, because only the
 * customer-orders view has one — that view spans a history rather than a single day — and the column
 * is too narrow for the two side by side. Handed them separately, the row stacks the date above the
 * time and the column keeps the one width it has in every list.
 */
export const getOrderTimeLabel = (order: OrderV2, customerOrdersView?: boolean): OrderTimeParts => {
  const selected = selectOrderTime(order, customerOrdersView);

  if (!selected) {
    return { time: '-', isPreorder: false };
  }

  return {
    time: dayjs(selected.value).format(FinnishShortTimeFormat),
    date: customerOrdersView ? dayjs(selected.value).format(FinnishDateFormat) : undefined,
    isPreorder: selected.isPreorder,
  };
};

/**
 * The grid sorts itself through its own sort model; this orders the phone list, which has no such
 * mechanism — newest first by default, and either way round once the phone's sort control has a say.
 */
export const sortOrdersByPlaced = <T extends OrderV2>(orders: T[], direction: 'asc' | 'desc' = 'desc'): T[] =>
  [...orders].sort((a, b) => {
    const difference = dayjs(a.orderPlaced).valueOf() - dayjs(b.orderPlaced).valueOf();

    return direction === 'asc' ? difference : -difference;
  });
